import { Lock, User as UserIcon, Building2, AlertCircle, CheckCircle, ArrowUp, ArrowDown, Key, Copy, RefreshCw, Car } from 'lucide-react'
import { useState, useEffect } from 'react'
import type { User, Tenant, Driver, UserRole } from '@/types'
import { tenantApi } from '@/api'
import { driverApi } from '@/api/drivers'
import apiClient from '@/api/client'
import { leaveApi, LeaveType } from '@/api/leaves'

// DEBUG: 幫助調試設置頁面功能
// 打開瀏覽器開發者工具 (F12)，在控制台看以下日誌：
// 1. 從 API 載入的租戶資料 - 確認租戶 ID 和初始數據
// 2. 準備發送的數據 - 檢查要提交的數據格式
// 3. 租戶 ID - 驗證租戶 ID 是否正確
// 4. API 返回結果 - 確認保存成功
// 5. 重新加載的租戶資訊 - 驗證數據已保存

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'tenant'>('profile')
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [driverInfo, setDriverInfo] = useState<Driver | null>(null)

  // Profile Form
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
  })

  // Password Form
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  })

  // Tenant Config Form
  const [tenantForm, setTenantForm] = useState({
    short_name: '',
    vehicle_categories: [] as Array<{ name: string; order?: number }>,
    payment_methods: [] as Array<{ name: string; order?: number }>,
    document_templates: '',
    external_api_key: '',
    hide_dispatch_amount: false,
    pricing_is_tax_inclusive_default: true,  // 車資預設：true=含稅, false=未稅
  })
  const [apiKeyCopied, setApiKeyCopied] = useState(false)
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([])
  const [newLeaveTypeName, setNewLeaveTypeName] = useState('')
  const [newLeaveTypeColor, setNewLeaveTypeColor] = useState('#6366f1')
  const [leaveTypeSubmitting, setLeaveTypeSubmitting] = useState(false)

  useEffect(() => {
    const loadUserData = async () => {
      try {
        console.log('=== 開始載入設置頁面數據 ===')

        // 從 localStorage 取得目前使用者資訊
        const userStr = localStorage.getItem('user')
        const tenantStr = localStorage.getItem('tenant')

        console.log('localStorage 中的用戶:', userStr ? JSON.parse(userStr) : null)
        console.log('localStorage 中的租戶:', tenantStr ? JSON.parse(tenantStr) : null)

        if (userStr) {
          const user = JSON.parse(userStr)
          setCurrentUser(user)
          setProfileForm({
            name: user.name,
            email: user.email,
            phone: user.phone,
          })
          if (user.driver_id) {
            try {
              const driver = await driverApi.getDriverById(user.driver_id)
              setDriverInfo(driver)
            } catch {
              // ignore
            }
          }
        }

        // 從 localStorage 取得租戶資訊，然後從 API 更新最新資料
        if (tenantStr) {
          const tenant = JSON.parse(tenantStr)
          console.log('從 localStorage 解析的租戶物件:', tenant)
          console.log('租戶 ID:', tenant.id)

          setCurrentTenant(tenant)

          try {
            // 從 API 取得租戶詳情（包括最新的車輛類別和付款方式）
            console.log('準備調用 API 獲取租戶詳情，ID:', tenant.id)
            const updatedTenant = await tenantApi.get(tenant.id)
            console.log('從 API 載入的租戶資料:', updatedTenant)
            setCurrentTenant(updatedTenant)
            
            // 解析 JSON 字串為陣列
            const parseJsonField = (value: any): any[] => {
              if (Array.isArray(value)) return value;
              if (typeof value === 'string') {
                try {
                  return JSON.parse(value);
                } catch {
                  return [];
                }
              }
              return [];
            };
            
            setTenantForm({
              short_name: updatedTenant.short_name || '',
              vehicle_categories: parseJsonField(updatedTenant.vehicle_categories),
              payment_methods: parseJsonField(updatedTenant.payment_methods),
              document_templates: typeof updatedTenant.document_templates === 'string' ? updatedTenant.document_templates : JSON.stringify(updatedTenant.document_templates || {}),
              external_api_key: updatedTenant.form_config?.external_api_key || '',
              hide_dispatch_amount: updatedTenant.form_config?.hide_dispatch_amount ?? false,
              pricing_is_tax_inclusive_default: updatedTenant.form_config?.pricing_is_tax_inclusive_default ?? true,
            })
          } catch (error: any) {
            console.error('從 API 載入租戶資訊失敗:', error)
            console.error('錯誤詳情:', error.response?.data || error.message)
            // 使用 localStorage 的備援資料
            // 解析 JSON 字串為陣列
            const parseJsonField = (value: any): any[] => {
              if (Array.isArray(value)) return value;
              if (typeof value === 'string') {
                try {
                  return JSON.parse(value);
                } catch {
                  return [];
                }
              }
              return [];
            };
            setTenantForm({
              short_name: tenant.short_name || '',
              vehicle_categories: parseJsonField(tenant.vehicle_categories),
              payment_methods: parseJsonField(tenant.payment_methods),
              document_templates: typeof tenant.document_templates === 'string' ? tenant.document_templates : JSON.stringify(tenant.document_templates || {}),
              external_api_key: tenant.form_config?.external_api_key || '',
              hide_dispatch_amount: tenant.form_config?.hide_dispatch_amount ?? false,
              pricing_is_tax_inclusive_default: tenant.form_config?.pricing_is_tax_inclusive_default ?? true,
            })
          }
        } else {
          console.warn('localStorage 中沒有租戶信息')
        }
      } catch (error) {
        console.error('載入使用者資訊失敗:', error)
      } finally {
        setLoading(false)
      }
    }

    loadUserData()

    const fetchLeaveTypes = async () => {
      try {
        const types = await leaveApi.listLeaveTypes()
        setLeaveTypes(types)
      } catch {
        // ignore
      }
    }
    fetchLeaveTypes()
  }, [])

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    setSubmitting(true)
    try {
      if (currentUser) {
        const updatedUser = { ...currentUser, ...profileForm }
        localStorage.setItem('user', JSON.stringify(updatedUser))
        setCurrentUser(updatedUser)
      }
      setMessage({ type: 'success', text: '個人資訊已儲存' })
    } catch (error) {
      setMessage({ type: 'error', text: '儲存失敗，請重試' })
    } finally {
      setSubmitting(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setMessage({ type: 'error', text: '新密碼與確認密碼不一致' })
      return
    }

    if (passwordForm.new_password.length < 6) {
      setMessage({ type: 'error', text: '新密碼長度至少 6 個字元' })
      return
    }

    setSubmitting(true)
    try {
      await apiClient.post('/users/me/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      })
      setMessage({ type: 'success', text: '密碼已變更' })
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: '',
      })
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.response?.data?.detail || '密碼變更失敗，請檢查當前密碼' })
    } finally {
      setSubmitting(false)
    }
  }

  // 上下移動列表項目的輔助函數
  const moveVehicleCategory = (index: number, direction: 'up' | 'down') => {
    const categories = [...tenantForm.vehicle_categories]
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= categories.length) return

    [categories[index], categories[newIndex]] = [categories[newIndex], categories[index]]
    setTenantForm({ ...tenantForm, vehicle_categories: categories })
  }

  const movePaymentMethod = (index: number, direction: 'up' | 'down') => {
    const methods = [...tenantForm.payment_methods]
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= methods.length) return

    [methods[index], methods[newIndex]] = [methods[newIndex], methods[index]]
    setTenantForm({ ...tenantForm, payment_methods: methods })
  }


  const handleTenantSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)

    if (!currentTenant) {
      setMessage({ type: 'error', text: '租戶資訊未載入' })
      return
    }

    setSubmitting(true)
    try {
      const existingFormConfig = currentTenant.form_config || {}
      const updateData = {
        short_name: tenantForm.short_name,
        vehicle_categories: tenantForm.vehicle_categories,
        payment_methods: tenantForm.payment_methods,
        document_templates: tenantForm.document_templates ? JSON.parse(tenantForm.document_templates) : null,
        form_config: {
          ...existingFormConfig,
          external_api_key: tenantForm.external_api_key || undefined,
          hide_dispatch_amount: tenantForm.hide_dispatch_amount,
          pricing_is_tax_inclusive_default: tenantForm.pricing_is_tax_inclusive_default,
        },
      }

      const result = await tenantApi.update(currentTenant.id, updateData)

      const messages = ['租戶設定已儲存']
      if (tenantForm.hide_dispatch_amount !== (currentTenant.form_config?.hide_dispatch_amount ?? false)) {
        messages.push(tenantForm.hide_dispatch_amount ? '派車單金額已隱藏' : '派車單金額已顯示')
      }
      setMessage({ type: 'success', text: messages[messages.length - 1] })
      const updatedTenant = await tenantApi.get(currentTenant.id)
      setCurrentTenant(updatedTenant)
      localStorage.setItem('tenant', JSON.stringify(updatedTenant))
    } catch (error: any) {
      setMessage({ type: 'error', text: '儲存失敗，請重試' })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">載入中...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">設定</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          管理你的帳戶和租戶設定
        </p>
      </div>

      {message && (
        <div className={`flex items-center gap-3 rounded-lg p-4 ${message.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
          {message.type === 'success' ? <CheckCircle className="h-5 w-5 flex-shrink-0" /> : <AlertCircle className="h-5 w-5 flex-shrink-0" />}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        <button onClick={() => setActiveTab('profile')} className={`flex items-center gap-2 px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'profile' ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400' : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}>
          <UserIcon className="h-4 w-4" /> 個人資訊
        </button>
        <button onClick={() => setActiveTab('password')} className={`flex items-center gap-2 px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'password' ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400' : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}>
          <Lock className="h-4 w-4" /> 變更密碼
        </button>
        {currentUser?.role !== 'driver' && (
          <button onClick={() => setActiveTab('tenant')} className={`flex items-center gap-2 px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'tenant' ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400' : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}>
            <Building2 className="h-4 w-4" /> 租戶設定
          </button>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        {activeTab === 'profile' && (
          <form onSubmit={handleProfileSubmit} className="space-y-4 max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">個人資訊</h2>

            {/* 姓名 + 電話同一列 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">姓名</label>
                <input type="text" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">電話</label>
                <input type="tel" value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">電子郵件</label>
              <input type="email" value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            </div>

            <button type="submit" disabled={submitting} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">{submitting ? '儲存中...' : '儲存修改'}</button>

            {/* 綁定車輛資訊（司機才顯示） */}
            {driverInfo && (() => {
              const allVehicles = [
                { plate: driverInfo.vehicle_plate, type: driverInfo.vehicle_type, brand_model: driverInfo.vehicle_brand_model, color: driverInfo.vehicle_color },
                ...((driverInfo.vehicles || []) as { plate: string; type?: string; brand_model?: string; color?: string }[])
                  .filter(v => v.plate && v.plate !== driverInfo.vehicle_plate),
              ]
              return (
                <div className="mt-6 space-y-3 border-t border-gray-200 dark:border-gray-700 pt-5">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <Car className="h-4 w-4 text-gray-500" />
                    綁定車輛
                    <span className="text-xs font-normal text-gray-400">（{allVehicles.length} 輛）</span>
                  </h3>
                  <div className="space-y-2">
                    {allVehicles.map((v, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
                        <span className="font-mono font-bold text-gray-900 dark:text-white text-sm">{v.plate}</span>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">|</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{v.type || '-'}</span>
                        {v.brand_model && <><span className="text-gray-500 dark:text-gray-400 text-xs">|</span><span className="text-sm text-gray-600 dark:text-gray-400">{v.brand_model}</span></>}
                        {v.color && <span className="ml-auto text-xs text-gray-400">{v.color}</span>}
                        {i === 0 && <span className="ml-auto text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded">主車</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}
          </form>
        )}

        {activeTab === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">變更密碼</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">當前密碼</label>
              <input type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">新密碼</label>
              <input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">確認新密碼</label>
              <input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            </div>
            <button type="submit" disabled={submitting} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">{submitting ? '變更中...' : '變更密碼'}</button>
          </form>
        )}

        {activeTab === 'tenant' && currentUser?.role !== 'driver' && (
          <form onSubmit={handleTenantSubmit} className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">租戶設定 - {currentTenant?.name}</h2>

            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">商戶短名稱</label>
                {currentTenant?.tax_id && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    統一編號（司機預設密碼）：<span className="font-mono font-medium text-gray-700 dark:text-gray-300">{currentTenant.tax_id.trim()}</span>
                  </span>
                )}
              </div>
              <input type="text" value={tenantForm.short_name} onChange={(e) => setTenantForm({ ...tenantForm, short_name: e.target.value })} placeholder="例：台北分公司" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">車輛類別</h3>
              <div className="space-y-2">
                {tenantForm.vehicle_categories.map((cat, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input type="text" value={cat.name} onChange={(e) => { const updated = [...tenantForm.vehicle_categories]; updated[idx].name = e.target.value; setTenantForm({ ...tenantForm, vehicle_categories: updated }) }} placeholder="例：轎車、SUV" className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                    <button type="button" onClick={() => moveVehicleCategory(idx, 'up')} disabled={idx === 0} className="rounded-lg bg-gray-100 px-2 py-2 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors" title="上移"><ArrowUp className="h-4 w-4" /></button>
                    <button type="button" onClick={() => moveVehicleCategory(idx, 'down')} disabled={idx === tenantForm.vehicle_categories.length - 1} className="rounded-lg bg-gray-100 px-2 py-2 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors" title="下移"><ArrowDown className="h-4 w-4" /></button>
                    <button type="button" onClick={() => { setTenantForm({ ...tenantForm, vehicle_categories: tenantForm.vehicle_categories.filter((_, i) => i !== idx) }) }} className="rounded-lg bg-red-100 px-3 py-2 text-red-600 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 dark:hover:bg-red-900 transition-colors">移除</button>
                  </div>
                ))}
                <button type="button" onClick={() => { setTenantForm({ ...tenantForm, vehicle_categories: [...tenantForm.vehicle_categories, { name: '', order: tenantForm.vehicle_categories.length }] }) }} className="w-full rounded-lg border border-blue-300 bg-blue-50 py-2 text-sm text-blue-600 hover:bg-blue-100 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 transition-colors">+ 新增車輛類別</button>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">付款方式</h3>
              <div className="space-y-2">
                {tenantForm.payment_methods.map((method, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input type="text" value={method.name} onChange={(e) => { const updated = [...tenantForm.payment_methods]; updated[idx].name = e.target.value; setTenantForm({ ...tenantForm, payment_methods: updated }) }} placeholder="例：現金、信用卡" className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                    <button type="button" onClick={() => movePaymentMethod(idx, 'up')} disabled={idx === 0} className="rounded-lg bg-gray-100 px-2 py-2 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors" title="上移"><ArrowUp className="h-4 w-4" /></button>
                    <button type="button" onClick={() => movePaymentMethod(idx, 'down')} disabled={idx === tenantForm.payment_methods.length - 1} className="rounded-lg bg-gray-100 px-2 py-2 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors" title="下移"><ArrowDown className="h-4 w-4" /></button>
                    <button type="button" onClick={() => { setTenantForm({ ...tenantForm, payment_methods: tenantForm.payment_methods.filter((_, i) => i !== idx) }) }} className="rounded-lg bg-red-100 px-3 py-2 text-red-600 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 dark:hover:bg-red-900 transition-colors">移除</button>
                  </div>
                ))}
                <button type="button" onClick={() => { setTenantForm({ ...tenantForm, payment_methods: [...tenantForm.payment_methods, { name: '', order: tenantForm.payment_methods.length }] }) }} className="w-full rounded-lg border border-green-300 bg-green-50 py-2 text-sm text-green-600 hover:bg-green-100 dark:border-green-700 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 transition-colors">+ 新增付款方式</button>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">文檔樣板配置 (JSON)</h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">配置派車單和出租單的樣板檔路徑和欄位座標</p>
              <textarea
                value={tenantForm.document_templates}
                onChange={(e) => setTenantForm({ ...tenantForm, document_templates: e.target.value })}
                placeholder='{"dispatch": {"template_path": "/app/templates/documents/dispatch_template.jpg", ...}, "rental": {...}}'
                className="w-full h-48 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-mono text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="space-y-3">
              <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                <Key className="h-4 w-4 text-gray-500" /> 外部 API Key
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                外部系統建立派車單時，在 Header 帶入 <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">X-Api-Key: &lt;key&gt;</code>，
                POST 至 <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">/api/v1/public/external/orders</code>
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tenantForm.external_api_key}
                  onChange={(e) => setTenantForm({ ...tenantForm, external_api_key: e.target.value })}
                  placeholder="尚未設定 API Key"
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-mono text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  readOnly
                />
                <button
                  type="button"
                  title="複製"
                  onClick={() => { navigator.clipboard.writeText(tenantForm.external_api_key); setApiKeyCopied(true); setTimeout(() => setApiKeyCopied(false), 2000) }}
                  disabled={!tenantForm.external_api_key}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                >
                  {apiKeyCopied ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  title="產生新 Key"
                  onClick={() => setTenantForm({ ...tenantForm, external_api_key: crypto.randomUUID() })}
                  className="rounded-lg border border-blue-300 px-3 py-2 text-blue-600 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-amber-600 dark:text-amber-400">產生新 Key 後需按「儲存設定」才會生效，舊 Key 立即失效。</p>
            </div>

            {/* 派車單金額顯示 */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">派車單設定</h3>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setTenantForm({ ...tenantForm, hide_dispatch_amount: !tenantForm.hide_dispatch_amount })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                    !tenantForm.hide_dispatch_amount ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    !tenantForm.hide_dispatch_amount ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    派車單顯示金額
                    <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                      !tenantForm.hide_dispatch_amount
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {!tenantForm.hide_dispatch_amount ? '開啟' : '關閉'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {!tenantForm.hide_dispatch_amount
                      ? '現金顯示金額數字，非現金顯示「-」'
                      : '金額欄位完全隱藏（不管付款方式）'}
                  </div>
                </div>
              </div>
            </div>

            {/* 車資預設設定 */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">車資預設</h3>
              <div className="flex items-center gap-3">
                <select
                  value={tenantForm.pricing_is_tax_inclusive_default ? 'true' : 'false'}
                  onChange={(e) => setTenantForm({ ...tenantForm, pricing_is_tax_inclusive_default: e.target.value === 'true' })}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="true">含稅（預設勾選「含稅」）</option>
                  <option value="false">未稅（預設勾選「未稅」）</option>
                </select>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                建單時「車資」旁的「含稅|未稅」按鈕預設狀態
              </div>
            </div>

            {/* 假別管理 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">假別管理</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                設定司機請假時可選用的假別，刪除假別前請確認無相關請假記錄。
              </p>
              <div className="space-y-2">
                {leaveTypes.map((lt) => (
                  <div key={lt.id} className="flex items-center gap-2">
                    <span
                      className="w-5 h-5 rounded flex-shrink-0 border border-gray-200"
                      style={{ backgroundColor: lt.color }}
                    />
                    <span className="flex-1 text-sm text-gray-800 dark:text-gray-200">{lt.name}</span>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!confirm(`確定刪除假別「${lt.name}」？`)) return
                        try {
                          await leaveApi.deleteLeaveType(lt.id)
                          setLeaveTypes((prev) => prev.filter((t) => t.id !== lt.id))
                        } catch {
                          setMessage({ type: 'error', text: '刪除失敗，請確認無相關請假記錄' })
                        }
                      }}
                      className="rounded-lg bg-red-100 px-2 py-1 text-xs text-red-600 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 transition-colors"
                    >
                      移除
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={newLeaveTypeColor}
                  onChange={(e) => setNewLeaveTypeColor(e.target.value)}
                  className="h-9 w-9 rounded border border-gray-300 cursor-pointer p-0.5"
                  title="選擇顏色"
                />
                <input
                  type="text"
                  value={newLeaveTypeName}
                  onChange={(e) => setNewLeaveTypeName(e.target.value)}
                  placeholder="假別名稱，例：病假"
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <button
                  type="button"
                  disabled={!newLeaveTypeName.trim() || leaveTypeSubmitting}
                  onClick={async () => {
                    if (!newLeaveTypeName.trim()) return
                    setLeaveTypeSubmitting(true)
                    try {
                      const created = await leaveApi.createLeaveType({
                        name: newLeaveTypeName.trim(),
                        color: newLeaveTypeColor,
                      })
                      setLeaveTypes((prev) => [...prev, created])
                      setNewLeaveTypeName('')
                      setNewLeaveTypeColor('#6366f1')
                    } catch {
                      setMessage({ type: 'error', text: '新增假別失敗' })
                    } finally {
                      setLeaveTypeSubmitting(false)
                    }
                  }}
                  className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {leaveTypeSubmitting ? '新增中...' : '+ 新增'}
                </button>
              </div>
            </div>

            <button type="submit" disabled={submitting} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">{submitting ? '儲存中...' : '儲存設定'}</button>
          </form>
        )}
      </div>
    </div>
  )
}